import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { resolveImageUrl } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Briefcase, Plus, Trash2, Edit, Eye, Users, DollarSign,
  MapPin, Clock, ChevronDown, ChevronUp, CheckCircle, XCircle,
  FileText, Sparkles, Zap, ArrowLeft, Send, Star, Bot, Upload,
  Image, X, Loader2, Wand2
} from 'lucide-react';

const JOB_TYPES = [
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'remote', label: 'Remote' },
  { value: 'internship', label: 'Internship' },
];

const JOB_TEMPLATES = [
  { label: 'General', desc: 'Start from scratch', template: {} },
  {
    label: 'Sales Rep',
    desc: 'Sales & business development role',
    template: {
      title: 'Sales Representative',
      description: 'We are looking for a motivated Sales Representative to grow our client base and revenue. You will identify prospects, build relationships, and close deals.',
      requirements: '- 2+ years of sales experience\n- Excellent communication skills\n- Self-motivated and goal-oriented\n- CRM experience preferred',
      benefits: '- Competitive base salary + commission\n- Health & dental insurance\n- Flexible work schedule\n- Growth opportunities',
      screening_questions: ['What is your sales experience?', 'What is your expected compensation?'],
    }
  },
  {
    label: 'Office Admin',
    desc: 'Administrative & office support',
    template: {
      title: 'Office Administrator',
      description: 'We need an organized Office Administrator to manage daily operations, scheduling, and communications. The ideal candidate is detail-oriented and professional.',
      requirements: '- Strong organizational skills\n- Proficiency in Microsoft Office / Google Workspace\n- Professional phone and email etiquette\n- 1+ years of office experience',
      benefits: '- Competitive salary\n- Paid time off\n- Health benefits\n- Friendly team environment',
      screening_questions: ['Describe your office management experience.', 'What software tools are you proficient with?'],
    }
  },
  {
    label: 'Marketing',
    desc: 'Marketing & social media role',
    template: {
      title: 'Marketing Coordinator',
      description: 'Join our team as a Marketing Coordinator to help execute campaigns, manage social media, and drive brand awareness.',
      requirements: '- Degree in Marketing or related field\n- Social media management experience\n- Strong writing and creative skills\n- Analytics and reporting experience',
      benefits: '- Creative work environment\n- Professional development budget\n- Flexible hours\n- Health & wellness benefits',
      screening_questions: ['Share a marketing campaign you managed.', 'What social platforms are you experienced with?'],
    }
  },
];

// ==================== JOB POST FORM ====================
const JobPostForm = ({ onSave, onCancel, initialData }) => {
  const [form, setForm] = useState({
    title: '', description: '', location: '', job_type: 'full_time',
    salary_range: '', requirements: '', benefits: '', screening_questions: [''],
    cover_image_url: '',
    ...initialData,
  });
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [imageMode, setImageMode] = useState(initialData?.cover_image_url ? 'preview' : 'none'); // none, upload, ai, preview
  const [imageGenerating, setImageGenerating] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [aiImagePrompt, setAiImagePrompt] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (initialData?.screening_questions?.length === 0) {
      setForm(f => ({ ...f, screening_questions: [''] }));
    }
  }, [initialData]);

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleQuestionChange = (index, value) => {
    const qs = [...form.screening_questions];
    qs[index] = value;
    setForm(f => ({ ...f, screening_questions: qs }));
  };

  const addQuestion = () => {
    if (form.screening_questions.length < 5) {
      setForm(f => ({ ...f, screening_questions: [...f.screening_questions, ''] }));
    }
  };

  const removeQuestion = (index) => {
    setForm(f => ({ ...f, screening_questions: f.screening_questions.filter((_, i) => i !== index) }));
  };

  const applyTemplate = (template) => {
    setForm(f => ({ ...f, ...template, screening_questions: template.screening_questions || [''] }));
    toast.success('Template applied!');
  };

  // AI Generate full job post
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return toast.error('Describe what you\'re hiring for');
    setAiGenerating(true);
    try {
      const res = await api.aiGenerateJob(aiPrompt.trim());
      if (res.success && res.job) {
        const job = res.job;
        setForm(f => ({
          ...f,
          title: job.title || f.title,
          description: job.description || f.description,
          requirements: job.requirements || f.requirements,
          benefits: job.benefits || f.benefits,
          salary_range: job.salary_range || f.salary_range,
          job_type: job.job_type || f.job_type,
          location: job.location || f.location,
          screening_questions: job.screening_questions?.length ? job.screening_questions : f.screening_questions,
        }));
        toast.success('AI generated your job post! Review and edit as needed.');
      } else {
        toast.error(res.error || 'AI generation failed');
      }
    } catch {
      toast.error('Failed to generate. Try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  // Image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setImageUploading(true);
    try {
      const res = await api.uploadImage(file);
      setForm(f => ({ ...f, cover_image_url: res.url }));
      setImageMode('preview');
      toast.success('Image uploaded!');
    } catch {
      toast.error('Upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  // AI generate image
  const handleAIImage = async () => {
    const prompt = aiImagePrompt.trim() || form.title || 'professional job posting';
    setImageGenerating(true);
    try {
      const res = await api.aiGenerateJobImage(prompt);
      if (res.success && res.image_url) {
        setForm(f => ({ ...f, cover_image_url: res.image_url }));
        setImageMode('preview');
        toast.success('AI image generated!');
      } else {
        toast.error(res.error || 'Image generation failed');
      }
    } catch {
      toast.error('Image generation failed');
    } finally {
      setImageGenerating(false);
    }
  };

  const removeImage = () => {
    setForm(f => ({ ...f, cover_image_url: '' }));
    setImageMode('none');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = () => {
    if (!form.title.trim()) return toast.error('Job title is required');
    if (!form.description.trim()) return toast.error('Description is required');
    const cleanQuestions = form.screening_questions.filter(q => q.trim());
    onSave({ ...form, screening_questions: cleanQuestions });
  };

  return (
    <div className="space-y-6" data-testid="job-post-form">
      {/* AI Job Generator */}
      <div className="bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20 rounded-xl p-4 border border-violet-200 dark:border-violet-800">
        <div className="flex items-center gap-2 mb-2">
          <Bot className="w-4 h-4 text-violet-600" />
          <span className="text-sm font-semibold text-violet-900 dark:text-violet-200">AI Job Post Generator</span>
        </div>
        <p className="text-xs text-violet-700 dark:text-violet-400 mb-3">
          Describe what you're hiring for and AI will create a complete job post for you
        </p>
        <div className="flex gap-2">
          <Textarea
            value={aiPrompt}
            onChange={e => setAiPrompt(e.target.value)}
            placeholder="e.g. We need a senior insurance agent who specializes in commercial property insurance, must have 5+ years experience and P&C license..."
            className="min-h-[60px] text-sm bg-white dark:bg-background flex-1"
            data-testid="ai-job-prompt"
          />
        </div>
        <Button
          onClick={handleAIGenerate}
          disabled={aiGenerating}
          className="mt-2 bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="ai-generate-job-btn"
        >
          {aiGenerating ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating...</>
          ) : (
            <><Wand2 className="w-3.5 h-3.5 mr-1.5" />Generate Job Post with AI</>
          )}
        </Button>
      </div>

      {/* Templates */}
      <div>
        <label className="text-sm font-medium mb-2 block">Quick Start Templates</label>
        <div className="flex flex-wrap gap-2">
          {JOB_TEMPLATES.map(t => (
            <Button key={t.label} variant="outline" size="sm" className="text-xs h-8"
              onClick={() => applyTemplate(t.template)} data-testid={`template-${t.label.toLowerCase()}`}>
              <Sparkles className="w-3 h-3 mr-1" />{t.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm font-medium mb-1 block">Job Title *</label>
          <Input value={form.title} onChange={e => handleChange('title', e.target.value)}
            placeholder="e.g. Sales Representative" data-testid="job-title-input" />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Job Type</label>
          <select value={form.job_type} onChange={e => handleChange('job_type', e.target.value)}
            className="w-full h-10 px-3 border border-input rounded-md text-sm bg-background"
            data-testid="job-type-select">
            {JOB_TYPES.map(jt => <option key={jt.value} value={jt.value}>{jt.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Location</label>
          <Input value={form.location} onChange={e => handleChange('location', e.target.value)}
            placeholder="e.g. Dallas, TX or Remote" data-testid="job-location-input" />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Salary Range</label>
          <Input value={form.salary_range} onChange={e => handleChange('salary_range', e.target.value)}
            placeholder="e.g. $50,000 - $70,000" data-testid="job-salary-input" />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium mb-1 block">Job Description *</label>
        <Textarea value={form.description} onChange={e => handleChange('description', e.target.value)}
          placeholder="Describe the role, responsibilities, and what a typical day looks like..."
          className="min-h-[120px]" data-testid="job-description-input" />
      </div>

      <div>
        <label className="text-sm font-medium mb-1 block">Requirements</label>
        <Textarea value={form.requirements} onChange={e => handleChange('requirements', e.target.value)}
          placeholder="- 2+ years experience&#10;- Strong communication skills&#10;- Bachelor's degree preferred"
          className="min-h-[80px]" data-testid="job-requirements-input" />
      </div>

      <div>
        <label className="text-sm font-medium mb-1 block">Benefits</label>
        <Textarea value={form.benefits} onChange={e => handleChange('benefits', e.target.value)}
          placeholder="- Health insurance&#10;- 401(k) match&#10;- Flexible hours"
          className="min-h-[80px]" data-testid="job-benefits-input" />
      </div>

      {/* Cover Image */}
      <div>
        <label className="text-sm font-medium mb-2 block">Cover Image (optional)</label>
        <input type="file" ref={fileInputRef} className="hidden" accept="image/jpeg,image/png,image/webp"
          onChange={handleImageUpload} data-testid="cover-image-file-input" />

        {imageMode === 'preview' && form.cover_image_url ? (
          <div className="relative rounded-lg overflow-hidden border border-border">
            <img
              src={resolveImageUrl(form.cover_image_url)}
              alt="Cover"
              className="w-full h-40 object-cover"
            />
            <Button
              variant="ghost" size="sm"
              className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 h-7 w-7"
              onClick={removeImage}
              data-testid="remove-cover-image"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : imageMode === 'ai' ? (
          <div className="space-y-2 p-3 bg-muted/30 rounded-lg border border-border">
            <Input
              value={aiImagePrompt}
              onChange={e => setAiImagePrompt(e.target.value)}
              placeholder={form.title || 'Describe the image you want...'}
              className="text-sm"
              data-testid="ai-image-prompt"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAIImage} disabled={imageGenerating}
                className="text-xs bg-violet-600 hover:bg-violet-700" data-testid="ai-generate-image-btn">
                {imageGenerating ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Generating...</>
                ) : (
                  <><Wand2 className="w-3 h-3 mr-1" />Generate Image</>
                )}
              </Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => setImageMode('none')}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="text-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageUploading}
              data-testid="upload-cover-image-btn">
              {imageUploading ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Uploading...</>
              ) : (
                <><Upload className="w-3 h-3 mr-1" />Upload Image</>
              )}
            </Button>
            <Button variant="outline" size="sm" className="text-xs"
              onClick={() => setImageMode('ai')}
              data-testid="ai-image-mode-btn">
              <Wand2 className="w-3 h-3 mr-1" />AI Generate Image
            </Button>
          </div>
        )}
      </div>

      {/* Screening Questions */}
      <div>
        <label className="text-sm font-medium mb-2 block">Screening Questions (max 5)</label>
        <p className="text-xs text-muted-foreground mb-2">Applicants will answer these when applying — helps you sort candidates</p>
        {form.screening_questions.map((q, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <Input value={q} onChange={e => handleQuestionChange(i, e.target.value)}
              placeholder={`Question ${i + 1}`} data-testid={`screening-q-${i}`} />
            {form.screening_questions.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => removeQuestion(i)} className="text-red-500 px-2">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
        {form.screening_questions.length < 5 && (
          <Button variant="outline" size="sm" onClick={addQuestion} className="text-xs" data-testid="add-question-btn">
            <Plus className="w-3 h-3 mr-1" />Add Question
          </Button>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" onClick={onCancel} data-testid="cancel-job-btn">Cancel</Button>
        <Button onClick={handleSubmit} data-testid="save-job-btn">
          <Send className="w-4 h-4 mr-2" />{initialData?.id ? 'Update' : 'Create'} Job Post
        </Button>
      </div>
    </div>
  );
};


// ==================== APPLICATIONS VIEW ====================
const ApplicationsView = ({ postId, onBack }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState(null);

  useEffect(() => {
    api.getJobPost(postId).then(data => { setPost(data.post); setLoading(false); }).catch(() => setLoading(false));
  }, [postId]);

  const updateStatus = async (appId, status) => {
    try {
      await api.updateApplicationStatus(appId, status);
      setPost(prev => ({
        ...prev,
        applications: prev.applications.map(a => a.id === appId ? { ...a, status } : a)
      }));
      toast.success(`Application ${status}`);
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading applications...</div>;
  if (!post) return <div className="text-center py-12">Post not found</div>;

  const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    reviewed: 'bg-yellow-100 text-yellow-800',
    shortlisted: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };

  return (
    <div data-testid="applications-view">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4" data-testid="back-to-posts">
        <ArrowLeft className="w-4 h-4 mr-1" />Back to Posts
      </Button>

      <div className="mb-6">
        <h2 className="text-lg font-bold">{post.title}</h2>
        <p className="text-sm text-muted-foreground">{post.application_count} application{post.application_count !== 1 ? 's' : ''}</p>
      </div>

      {post.applications?.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No applications yet</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {post.applications?.map(app => (
            <Card key={app.id} className="border-border/50" data-testid={`application-${app.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                      {app.applicant_photo ? (
                        <img src={app.applicant_photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-primary">{(app.applicant_name || 'A')[0]}</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{app.applicant_name}</p>
                      <p className="text-xs text-muted-foreground">{app.applicant_profession} {app.applicant_city && `· ${app.applicant_city}, ${app.applicant_state}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] ${statusColors[app.status] || ''}`}>{app.status}</Badge>
                    <Button variant="ghost" size="sm" className="p-1"
                      onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}>
                      {expandedApp === app.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {expandedApp === app.id && (
                  <div className="mt-4 pt-4 border-t border-border/50 space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-muted-foreground">Email:</span> {app.applicant_email}</div>
                      <div><span className="text-muted-foreground">Phone:</span> {app.applicant_phone || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Applied:</span> {new Date(app.applied_at).toLocaleDateString()}</div>
                      {app.resume_url && (
                        <div><a href={app.resume_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">View Resume</a></div>
                      )}
                    </div>

                    {app.answers?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold">Screening Answers:</p>
                        {app.answers.map((ans, i) => (
                          <div key={i} className="bg-muted/50 rounded-lg p-2">
                            <p className="text-xs font-medium text-muted-foreground">Q{i + 1}: {post.screening_questions?.[i] || `Question ${i + 1}`}</p>
                            <p className="text-sm mt-1">{typeof ans === 'object' ? ans.answer : ans}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" className="text-xs h-7"
                        onClick={() => updateStatus(app.id, 'reviewed')} data-testid={`mark-reviewed-${app.id}`}>
                        <Eye className="w-3 h-3 mr-1" />Reviewed
                      </Button>
                      <Button size="sm" className="text-xs h-7 bg-green-600 hover:bg-green-700"
                        onClick={() => updateStatus(app.id, 'shortlisted')} data-testid={`shortlist-${app.id}`}>
                        <Star className="w-3 h-3 mr-1" />Shortlist
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs h-7 text-red-600"
                        onClick={() => updateStatus(app.id, 'rejected')} data-testid={`reject-${app.id}`}>
                        <XCircle className="w-3 h-3 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ==================== TEAM ROSTER ====================
const TeamRoster = () => {
  const [employees, setEmployees] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTeam(); }, []);

  const loadTeam = async () => {
    try {
      const data = await api.getEmployees();
      setEmployees(data.employees || []);
      setPendingInvites(data.pending_invites || []);
    } catch {} finally { setLoading(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) return toast.error('Enter a valid email');
    setInviting(true);
    try {
      const res = await api.inviteEmployee(inviteEmail.trim());
      toast.success(res.message);
      setInviteEmail('');
      loadTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invite');
    } finally { setInviting(false); }
  };

  const handleRemove = async (empId, name) => {
    if (!window.confirm(`Remove ${name} from your team?`)) return;
    try {
      await api.removeEmployee(empId);
      toast.success('Employee removed');
      loadTeam();
    } catch { toast.error('Failed to remove'); }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await api.cancelInvite(inviteId);
      toast.success('Invite cancelled');
      loadTeam();
    } catch { toast.error('Failed to cancel'); }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading team...</div>;

  return (
    <div className="space-y-6" data-testid="team-roster">
      {/* Invite */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Invite Employee</CardTitle>
          <CardDescription className="text-xs">Send an email invite to add team members</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              placeholder="employee@email.com" className="flex-1" data-testid="invite-email-input"
              onKeyDown={e => e.key === 'Enter' && handleInvite()} />
            <Button onClick={handleInvite} disabled={inviting} data-testid="send-invite-btn">
              {inviting ? 'Sending...' : <><Send className="w-4 h-4 mr-1" />Invite</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground">Pending Invites</h3>
          <div className="space-y-2">
            {pendingInvites.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm">{inv.email}</span>
                  <Badge variant="secondary" className="text-[10px]">Pending</Badge>
                </div>
                <Button variant="ghost" size="sm" className="text-xs text-red-500 h-7" onClick={() => handleCancelInvite(inv.id)}>
                  Cancel
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team */}
      <div>
        <h3 className="text-sm font-semibold mb-2">{employees.length} Team Member{employees.length !== 1 ? 's' : ''}</h3>
        {employees.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No team members yet. Invite your first employee above.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {employees.map(emp => (
              <Card key={emp.id} className="border-border/50" data-testid={`employee-${emp.id}`}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                      {emp.profile_photo_url ? (
                        <img src={emp.profile_photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-primary">{(emp.full_name || 'E')[0]}</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{emp.full_name}</p>
                      <p className="text-xs text-muted-foreground">{emp.profession_name} {emp.city && `· ${emp.city}, ${emp.state}`}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs text-red-500 h-7"
                    onClick={() => handleRemove(emp.id, emp.full_name)} data-testid={`remove-emp-${emp.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" />Remove
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ==================== MAIN HIRING PAGE ====================
export default function HiringPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, create, edit, applications
  const [editingPost, setEditingPost] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [activated, setActivated] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activeTab, setActiveTab] = useState('jobs');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [postsData, statusData] = await Promise.all([
        api.getMyJobPosts(),
        api.getHiringActivation(),
      ]);
      setPosts(postsData.posts || []);
      setActivated(statusData.activated || false);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Business account required');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const result = await api.createCheckout('business_activation');
      if (result.url) window.location.href = result.url;
    } catch {
      toast.error('Failed to start activation');
    } finally {
      setActivating(false);
    }
  };

  const handleCreatePost = async (data) => {
    try {
      const result = await api.createJobPost(data);
      toast.success('Job post created! Pay to publish.');
      // Redirect to Stripe for job post payment
      const checkout = await api.createCheckout('job_post', result.post?.id);
      if (checkout.url) window.location.href = checkout.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create post');
    }
  };

  const handleUpdatePost = async (data) => {
    try {
      await api.updateJobPost(editingPost.id, data);
      toast.success('Job post updated');
      setView('list');
      loadData();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this job post? This cannot be undone.')) return;
    try {
      await api.deleteJobPost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Job post deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handlePayForPost = async (postId) => {
    try {
      const checkout = await api.createCheckout('job_post', postId);
      if (checkout.url) window.location.href = checkout.url;
    } catch {
      toast.error('Failed to start payment');
    }
  };

  if (user?.account_type !== 'business') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="hiring-business-only">
        <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-xl font-bold mb-2">Business Account Required</h1>
        <p className="text-muted-foreground">Switch to a business account in Settings to access hiring features.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 py-12 text-center text-muted-foreground">Loading...</div>;
  }

  // Activation Required
  if (!activated) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16" data-testid="hiring-activation-required">
        <Card className="border-2 border-primary/20">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-xl">Activate Your Business Account</CardTitle>
            <CardDescription>Unlock hiring features, employee management, and more</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-600" />Post job listings visible to all members</div>
              <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-600" />Receive and manage applications</div>
              <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-600" />"Hiring" badge on your business profile</div>
              <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-600" />Invite and manage employees</div>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">$20<span className="text-sm font-normal text-muted-foreground">/month</span></p>
            </div>
            <Button className="w-full h-11" onClick={handleActivate} disabled={activating} data-testid="activate-business-btn">
              {activating ? 'Redirecting...' : 'Activate Business Account'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="hiring-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Briefcase className="w-5 h-5" />Hiring Dashboard
          </h1>
        </div>
        {activeTab === 'jobs' && view === 'list' && (
          <Button onClick={() => { setEditingPost(null); setView('create'); }} data-testid="new-job-post-btn">
            <Plus className="w-4 h-4 mr-2" />New Job Post
            <span className="ml-2 text-xs opacity-70">$100/mo</span>
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-muted/50 rounded-lg w-fit">
        {[
          { id: 'jobs', label: 'Job Posts', icon: Briefcase, count: posts.length },
          { id: 'team', label: 'Team', icon: Users },
        ].map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setView('list'); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
            {tab.count > 0 && <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Team Tab */}
      {activeTab === 'team' && <TeamRoster />}

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <>
          {/* Create / Edit */}
      {(view === 'create' || view === 'edit') && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{view === 'edit' ? 'Edit Job Post' : 'Create Job Post'}</CardTitle>
            <CardDescription>$100/month keeps your post live in the feed with a hiring badge</CardDescription>
          </CardHeader>
          <CardContent>
            <JobPostForm
              onSave={view === 'edit' ? handleUpdatePost : handleCreatePost}
              onCancel={() => setView('list')}
              initialData={view === 'edit' ? editingPost : {}}
            />
          </CardContent>
        </Card>
      )}

      {/* Applications View */}
      {view === 'applications' && selectedPostId && (
        <ApplicationsView postId={selectedPostId} onBack={() => { setView('list'); loadData(); }} />
      )}

      {/* List */}
      {view === 'list' && (
        <>
          {posts.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Briefcase className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium">No job posts yet</p>
                <p className="text-sm text-muted-foreground mt-1">Create your first job post to start finding talent</p>
                <Button className="mt-4" onClick={() => setView('create')} data-testid="create-first-post-btn">
                  <Plus className="w-4 h-4 mr-2" />Create Job Post
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {posts.map(post => (
                <Card key={post.id} className="border-border/50 hover:border-border transition-colors overflow-hidden" data-testid={`job-post-${post.id}`}>
                  {post.cover_image_url && (
                    <div className="h-28 bg-muted">
                      <img src={resolveImageUrl(post.cover_image_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold truncate">{post.title}</h3>
                          <Badge variant={post.status === 'active' ? 'default' : 'secondary'} className="text-[10px]">
                            {post.status === 'pending_payment' ? 'Unpaid' : post.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {post.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{post.location}</span>}
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{JOB_TYPES.find(j => j.value === post.job_type)?.label || post.job_type}</span>
                          {post.salary_range && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{post.salary_range}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{post.description}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {post.status === 'pending_payment' ? (
                          <Button size="sm" className="text-xs h-8 bg-green-600 hover:bg-green-700"
                            onClick={() => handlePayForPost(post.id)} data-testid={`pay-post-${post.id}`}>
                            <DollarSign className="w-3 h-3 mr-1" />Pay $100
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="text-xs h-8"
                            onClick={() => { setSelectedPostId(post.id); setView('applications'); }}
                            data-testid={`view-apps-${post.id}`}>
                            <Users className="w-3 h-3 mr-1" />{post.application_count || 0}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-xs h-8 px-2"
                          onClick={() => { setEditingPost(post); setView('edit'); }}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-xs h-8 px-2 text-red-500"
                          onClick={() => handleDelete(post.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
      </>
      )}
    </div>
  );
}
